import { useEffect } from "react";
import { useState } from "react";
import { fetchAPI } from "../../utils/utils";
import moment from "moment";
import './NotificationLog.css';
import { getCastellerShortName } from "../../utils/castellerName";

function NotificationRecepients({ notifications, displayName }) {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const displayNames = notifications.map(n => n.target).map(displayName);
    const initialDisplayNames = displayNames.slice(0,4);
    const remainingDisplayNames = displayNames.slice(4);

    return (
        <>
            {isCollapsed ? initialDisplayNames.join(', ') : displayNames.join(', ')}
            {remainingDisplayNames.length > 0 && 
                <button
                    type="button"
                    className="inline-link-button"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    {isCollapsed ? ` i ${remainingDisplayNames.length} més` : ' mostrar menys'}
                </button>
            }
        </>
    )
}

function NotificationTaps({ notifications, castellersInfo }) {
    const [taps, setTaps] = useState([]);

    const totalReceptors = notifications.length

    const realReceptors = notifications
        .map(n => n.target)
        .filter(id => id in castellersInfo)
        .map(id => castellersInfo[id])
        .filter(c => c?.has_notifications)
        .length

    const lostReceptors = totalReceptors - realReceptors

    useEffect(() => {
        const notificationId = notifications?.[0]?.notification_id;
        if (!notificationId) return;

        fetchAPI(`/notification_taps/${notificationId}`, data => {
            setTaps(data);
        });
    }, [notifications?.[0]?.notification_id]);

    // Put '-' if notification is sent before the release date of the change
    const isBefore = notifications[0].createdAt < '2024-08-17T22:15:00Z';
    if (isBefore) return <span>-</span>

    const tapsMotes = taps
        .map(t => t.user_id)
        .filter(id => id in castellersInfo)
        .map(id => castellersInfo[id])
        .map(c => getCastellerShortName(c))
        .filter(mote => mote)
        .join(', ');

    return (
        <div title={tapsMotes} id={notifications?.[0]?.notification_id + '-taps'}>
            <a
                href={`#${notifications?.[0]?.notification_id}-taps`}
                onClick={() => alert(`L'han obert:\n\n${tapsMotes}`)}
                style={{
                    color: 'darkblue',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                }}
            >
                {taps.length} / {realReceptors}
            </a>
            {lostReceptors > 0 && <span style={{ color: 'red' }}> + {lostReceptors}</span>}
        </div>
    )
}

function NotificationLog({ castellersInfo, notificationsState }) {
    const [notificationIds, setNotificationIds] = useState([]);
    const [notificationsByGroup, setNotificationsByGroup] = useState({});
    const [newNotificationIds, setNewNotificationIds] = useState(new Set());
    const [loadNNotifications, setLoadNNotifications] = useState(10);
    const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        // Load notification IDs (paginated, grouped, merx_order_ filtered in backend)
        fetchAPI(`/notification_ids?limit=${loadNNotifications}`, ids => {
            setNotificationIds(prevIds => {
                // Only mark as new if this is not the initial load and there are actually new IDs
                if (!isInitialLoad && prevIds.length > 0) {
                    const prevSet = new Set(prevIds);
                    const newIds = ids.filter(id => !prevSet.has(id));
                    if (newIds.length > 0) {
                        setNewNotificationIds(new Set(newIds));
                    }
                }
                return ids;
            });

            setIsInitialLoad(false);

            // Load details for each notification group
            ids.forEach(notificationId => {
                if (notificationId && !(notificationId in notificationsByGroup)) {
                    fetchAPI(`/notifications/${encodeURIComponent(notificationId)}`, data => {
                        setNotificationsByGroup(prev => ({ ...prev, [notificationId]: data }));
                    });
                }
            });

            setHasMoreNotifications(ids.length === loadNNotifications);
        });
    }, [
        notificationsState === 'done',
        loadNNotifications
    ]);

    useEffect(() => {
        if (newNotificationIds.size > 0) {
            const id = setTimeout(() => {
                setNewNotificationIds(new Set());
            }, 5000);

            return () => clearTimeout(id);
        }
    }, [newNotificationIds.size])

    const displayName = id => castellersInfo[id] ?
        getCastellerShortName(castellersInfo[id]) :
        ''

    const dataEnviament = notification => {
        // Fa N segons
        const diffTime = Math.abs(moment() - moment(notification.createdAt));
        const diffSeconds = Math.floor(diffTime / 1000);
        if (diffSeconds < 60) return `Fa ${diffSeconds} segons`;
        // Fa N minuts
        const diffMinutes = Math.floor(diffSeconds / 60);
        if (diffMinutes < 60) return `Fa ${diffMinutes} minuts`;
        // Fa N hores i M minuts
        const diffHours = Math.floor(diffMinutes / 60);
        const diffMinutes2 = diffMinutes % 60;
        if (diffHours < 24) return `Fa ${diffHours} hores i ${diffMinutes2} minuts`;
        
        return moment(notification.createdAt).format('HH:mm DD/MM/YYYY')
    }

    return (
        <div>
            <h2>Historial de notificacions</h2>

            <div
                style={{
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <table
                    className="notifications-log"
                >
                    <thead>
                        <tr>
                            {/* <th>Id</th> */}
                            {/* <th>Usuari</th> */}
                            <th>Enviador</th>
                            <th>Receptors</th>
                            <th>Títol (exemple)</th>
                            <th>Missatge (exemple)</th>
                            <th>Data d'enviament</th>
                            <th>L'han obert</th>
                        </tr>
                    </thead>
                    <tbody
                        style={{
                            fontSize: '0.8rem',
                        }}
                    >
                        {
                            notificationIds
                                .filter(id => id in notificationsByGroup)
                                .map(notificationId => {
                                    const notifications = notificationsByGroup[notificationId];
                                    return (
                                        <tr
                                            key={notificationId}
                                            className={`notification ${newNotificationIds.has(notificationId) ? 'new' : ''}`}
                                        >
                                            {/* <td>{notificationId}</td> */}
                                            {/* <td>{notifications[0].target}</td> */}
                                            <td>{displayName(notifications[0].author)}</td>
                                            <td>
                                                <NotificationRecepients
                                                    notifications={notifications}
                                                    displayName={displayName}
                                                />
                                            </td>
                                            <td>{notifications[0].title}</td>
                                            <td style={{ whiteSpace: 'pre-line' }}>{notifications[0].body}</td>
                                            <td>
                                                {dataEnviament(notifications[0])}
                                            </td>
                                            <td>
                                                <NotificationTaps
                                                    notifications={notifications}
                                                    castellersInfo={castellersInfo}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                </table>
            </div>

            {
                hasMoreNotifications ? (
                    <button
                        onClick={() => setLoadNNotifications(loadNNotifications + 10)}
                        style={{
                            width: '100%',
                            marginTop: 10,
                        }}
                    >
                        Carrega'n més
                    </button>
                ) : null
            }
        </div>
    )
}

export default NotificationLog;
